import { Box, Divider, Flex, List, ListItem, SimpleGrid, Spinner, Text } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { BsBarChart, BsStopwatch } from 'react-icons/bs'
import { FiCheckCircle, FiMessageSquare } from 'react-icons/fi'
import { StatCard } from '../StatsCard'
import ChannelPerformanceChart from '../Charts/ChannelPerformanceChart'
import { FaCircle } from 'react-icons/fa6';
import DetailedChannelPerformanceSummary from './DetailSummary'
import ChartSkeleton from '../Charts/Skeleton'
import moment from 'moment'
import { formatDuration } from '../../../utils/DefaultFunction'
import API from '../../../helpers/axiosInstance'

const Performance = ({ date, refresh }) => {

    const [loading, setLoading] = useState(false);
    const [performanceData, setPerformanceData] = useState(null);

    const getApiHeaders = () => ({
        "x-api-key": localStorage.getItem("api-key"),
        "x-access-token": localStorage.getItem("customer-portal"),
    });
    const dateformated = moment(date);
    const month = dateformated.format("MM"); // "09"
    const year = dateformated.format("YYYY");

    const fetchPerformance = async () => {
        setLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/dashboard/customer/performance?month=${month}&year=${year}${refresh}`;
            const response = await API.get(url, { headers: getApiHeaders() });
            setPerformanceData(response.data.data);
        } catch (error) {
            console.error("Error fetching analytics:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPerformance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    if (loading || !performanceData) {
        return (
            <Flex justify="center" align="center" h="300px">
                <Spinner size="lg" />
            </Flex>
        );
    }

    return (
        <>
            <Box>
                <Flex gap={6} wrap="wrap" justifyContent="space-between">
                    <StatCard
                        title="Overall Delivery Rate"
                        value={`${performanceData?.deliveryRatePercent}%`}
                        isProgressBar
                        progressValue={performanceData?.deliveryRatePercent}
                        icon={<FiMessageSquare />}
                        iconColor="#64748b"
                    />
                    <StatCard
                        title="Overall Read Rate"
                        value={`${performanceData?.readRatePercent}%`}
                        isProgressBar
                        progressValue={performanceData?.readRatePercent}
                        changeColor="#00a63e"
                        icon={<FiCheckCircle />}
                        iconColor="#64748b"
                    />


                </Flex>
            </Box>

            <Box className="box-style-card" my={4}>
                <Flex direction="column" mb={4}>
                    <Flex align="center" gap={2}>
                        <BsBarChart size={20} color="#155dfc" />
                        <Text fontSize="md" fontWeight="semibold" color="gray.700">Channel Performance Comparison
                        </Text>
                    </Flex>
                    <Text fontSize="sm" color="#64748b">Delivery and read rates across all channels</Text>
                </Flex>
                {performanceData?.channelPerformance?.length > 0 ? <ChannelPerformanceChart data={performanceData?.channelPerformance} /> : <ChartSkeleton title={"Channel performance metrics will appear here"} />}

            </Box>
            {/* Journey Phase Break Down  */}
            <Box className="box-style-card" my={4}>
                <Flex justifyContent={"space-between"}>
                    <Flex direction="column" mb={4}>
                        <Flex align="center" gap={2}>
                            <BsStopwatch size={20} color="#155dfc" />
                            <Text fontSize="md" fontWeight="semibold" color="gray.700">Message Journey Timeline (24h Average)
                            </Text>
                        </Flex>
                        <Text fontSize="sm" color="#64748b">Average processing times across all phases from the last 24 hours - all times in seconds</Text>
                    </Flex>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700" align={"end"}>Last Updated
                        </Text>
                        <Text fontSize="xs" color="#64748b" align={"end"}>{moment().format('MMMM Do YYYY, h:mm:ss a')}</Text>
                    </Box>
                </Flex>

                {Object.entries(performanceData?.messageJourney).length > 0 ? <>
                    <Box
                        className="info-card"
                        bg={"var(--brand-tint)"}
                        border={"1px solid var(--brand-tint-strong)"}
                        borderRadius="15px"
                        p={5}
                        flex="1"
                        minW="200px"

                    >
                        <Flex direction="column" >
                            <Flex align="center" gap={2}>
                                <BsStopwatch size={15} color="#193cb8" />
                                <Text fontSize="xs" fontWeight="semibold" color="#193cb8">Data Period: Last 24 Hours • All Values are Averages in Seconds
                                </Text>
                            </Flex>
                            <Text fontSize="xs" color="var(--brand-primary-hover)">Journey tracking from message receipt to final confirmation • Voice has no read receipts • Push tracks clicks instead of reads</Text>
                        </Flex>
                    </Box>


                    <Text fontSize="md" fontWeight="semibold" color="gray.700" my={4}>Journey Phase Breakdown
                    </Text>
                    <Flex gap={6} wrap="wrap" justifyContent="space-between">
                        <Box
                            className="info-card"
                            bg={"var(--brand-tint)"}

                            borderRadius="15px"
                            p={5}
                            flex="1"
                            minW="200px"

                        >
                            <Flex alignItems={"center"} >
                                <Box me={3}>
                                    <FaCircle color='#2b7fff' />
                                </Box>
                                <Box>
                                    <Text fontSize="sm" color="#193cb8" fontWeight={"medium"}>Phase 1</Text>
                                    <Text fontSize="xs" color="var(--brand-primary-hover)">Received → Sent to Channel</Text>
                                    <Text fontSize="xs" color="var(--brand-primary-hover)">Processing, validation & channel routing
                                    </Text>
                                </Box>
                            </Flex>
                        </Box>
                        <Box
                            className="info-card"
                            bg={"#fff7ed"}

                            borderRadius="15px"
                            p={5}
                            flex="1"
                            minW="200px"

                        >
                            <Flex alignItems={"center"} >
                                <Box me={3}>
                                    <FaCircle color='#ff6900' />
                                </Box>
                                <Box>
                                    <Text fontSize="sm" color="#9f2d00" fontWeight={"medium"}>Phase 1</Text>
                                    <Text fontSize="xs" color="#f54a00">Received → Sent to Channel</Text>
                                    <Text fontSize="xs" color="#f54a00">Processing, validation & channel routing
                                    </Text>
                                </Box>
                            </Flex>
                        </Box>
                        <Box
                            className="info-card"
                            bg={"#faf5ff"}

                            borderRadius="15px"
                            p={5}
                            flex="1"
                            minW="200px"

                        >
                            <Flex alignItems={"center"} >
                                <Box me={3}>
                                    <FaCircle color='#ad46ff' />
                                </Box>
                                <Box>
                                    <Text fontSize="sm" color="#6e11b0" fontWeight={"medium"}>Phase 1</Text>
                                    <Text fontSize="xs" color="#9810fa">Received → Sent to Channel</Text>
                                    <Text fontSize="xs" color="#9810fa">Processing, validation & channel routing
                                    </Text>
                                </Box>
                            </Flex>
                        </Box>
                    </Flex>


                    <Box mt={4}>
                        <Text as="p" fontSize={"xs"} mb={4} color={"#64748b"}>
                            * Phase 3 availability varies by channel:
                        </Text>

                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>

                            <Box>
                                <List spacing={3}>
                                    <ListItem>
                                        <Flex>
                                            <Text as="span" fontWeight="bold" fontSize={"xs"} color={"#64748b"}>
                                                WhatsApp, Email, Inbox:
                                            </Text>
                                            <Text as="span" ml={2} fontSize={"xs"} color={"#64748b"}>
                                                Read receipts available
                                            </Text>
                                        </Flex>
                                    </ListItem>
                                    <ListItem>
                                        <Flex>
                                            <Text as="span" fontWeight="bold" fontSize={"xs"} color={"#64748b"}>
                                                SMS,Voice:
                                            </Text>
                                            <Text as="span" ml={2} fontSize={"xs"} color={"#64748b"}>
                                                No read receipts (Phase 3 not applicable)
                                            </Text>
                                        </Flex>
                                    </ListItem>
                                </List>
                            </Box>


                            <Box>
                                <List spacing={2}>
                                    <ListItem>
                                        <Flex>
                                            <Text as="span" fontWeight="bold" fontSize={"xs"} color={"#64748b"}>
                                                Push Notifications:
                                            </Text>
                                            <Text as="span" ml={2} fontSize={"xs"} color={"#64748b"}>
                                                Click tracking available
                                            </Text>
                                        </Flex>
                                    </ListItem>
                                </List>
                            </Box>
                        </SimpleGrid>
                    </Box>

                    <Divider my={8} />



                    <Box
                        p={6}
                        bg={"#f1f5f980"}
                        borderRadius={"md"}

                    >
                        {/* <Heading as="h2" size="md" mb={6} color="gray.700" fontWeight="normal">
                        24-Hour Performance Summary
                    </Heading> */}

                        {/* Use SimpleGrid for the four-column layout */}
                        <SimpleGrid
                            columns={{ base: 2, md: 4 }} // 2 columns on mobile, 4 on desktop
                            spacing={4}
                            textAlign="center"
                        >
                            {[
                                {
                                    value: `${performanceData?.messageJourneySummary?.phase_1}s`,
                                    label: 'Avg Phase 1 (All Channels)',
                                    color: '#155dfc', // Blue color for the value
                                },
                                {
                                    value: `${formatDuration(performanceData?.messageJourneySummary?.phase_2?.toFixed(2))}`,
                                    label: 'Avg Phase 2 (All Channels)',
                                    color: '#f54a00', // Orange color for the value
                                },
                                {
                                    value: `${formatDuration(performanceData?.messageJourneySummary?.phase_3?.toFixed(2))}`,
                                    label: 'Avg Phase 3 (Read/Click Enabled)',
                                    color: '#9810fa', // Purple color for the value
                                },
                                {
                                    value: `${performanceData?.messageJourneySummary?.channel_wo_phase_3}`,
                                    label: 'Channels w/o Phase 3',
                                    color: '#64748b', // Default color for the value
                                },
                            ].map((metric, index) => (
                                <Box key={index} py={2}>
                                    {/* The main metric value (large and colored) */}
                                    <Text
                                        fontSize={{ base: 'lg', md: 'lg' }}
                                        fontWeight="semibold"
                                        color={metric.color}
                                        mb={1}
                                    >
                                        {metric.value}
                                    </Text>

                                    {/* The descriptive label (smaller) */}
                                    <Text fontSize={{ base: 'xs', md: 'xs' }} color="#64748b">
                                        {metric.label}
                                    </Text>
                                </Box>
                            ))}
                        </SimpleGrid>
                    </Box>




                    <DetailedChannelPerformanceSummary data={performanceData?.messageJourney} />
                </> : <ChartSkeleton title={"No journey timeline data available"} />}

            </Box>

        </>
    )
}

export default Performance